import "./style.scss";

import { Expander } from "@amzn/stencil-react-components/expander";
import { Hr, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { trackStructEvent } from "@snowplow/browser-tracker";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

import { Logger } from "@/apis/Logger";
import { publishInsertAndTestSecurityKeyStepBypassedMetric } from "@/apis/Metrics";
import NextButton from "@/components/Commons/NextButton";
import { getOS } from "@/helpers/os-helper";
import { isUsbDetectionAvailable } from "@/hooks/useUsbDevices";
import { Day1EventAction } from "@/types/snowplow-events";

import { ActionType, AppContext } from "../../../stores";
import { getEnabledSteps } from "../../../stores/constants";
import {
  activeStepClassName,
  day1StepsGridViewPadding,
  STEP_ID,
} from "../../constants";
import { getStepHeader } from "../StepStatus/step-status";

/**
 * A guard a step can register to intercept the Next button click.
 * Runs before the step advances; resolve/return `true` to proceed or
 * `false` to stay on the step (e.g. to surface a warning first).
 * Guards must never throw to block — errors are treated as `true`
 * (fail open) so a broken guard can't trap the user on a step.
 */
export type StepNextGuard = () => Promise<boolean> | boolean;

/**
 * Lets a step register (or clear, with `null`) a {@link StepNextGuard} on its
 * containing StepContainer. Steps that never register a guard get today's
 * behavior: Next advances immediately.
 */
export const StepNextGuardContext = createContext<
  (guard: StepNextGuard | null) => void
>(() => undefined);
interface StepProps extends PropsWithChildren<unknown> {
  /**
   * Step ID assigned to each step.
   */
  stepId: STEP_ID;

  /**
   * The additional next handler that needs to be invoked besides setting the step as completed and increment
   * the current step number.
   */
  handleNext?: (...args: unknown[]) => void;

  /**
   * The data test id that will be assigned to the View of the step.
   */
  dataTestId?: string;
}

/**
 * Step container which holds the logic of advancing to the next step and collapses the expander after clicking the
 * next button. It will be used by all onBoarding steps on the Day1 wizard.
 * @param props A StepProps interface
 * @constructor
 */
export default function StepContainer(props: StepProps): JSX.Element | null {
  const intl = useIntl();
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const nextGuardRef = useRef<StepNextGuard | null>(null);
  const [isNextGuardPending, setIsNextGuardPending] = useState<boolean>(false);
  const { state, dispatch } = useContext(AppContext);

  const setNextGuard = useCallback((guard: StepNextGuard | null): void => {
    nextGuardRef.current = guard;
  }, []);
  const targetStepIdx: number = state.steps.findIndex(
    (step) => step.id === props.stepId
  );
  const targetStep = state.steps[targetStepIdx];

  /**
   * Set the target step to completed and collapses the expander if the target step exists in the global app state.
   *
   * The "section opened" journey metric is not recorded here. It describes the wizard
   * advancing, which this component cannot observe reliably because it is mounted and
   * unmounted as the active step changes; see useStepJourneyMetrics.
   */
  useEffect(() => {
    if (targetStep) {
      const isExpanded = state.currentStepNum === targetStepIdx + 1;
      setIsExpanded(isExpanded);
      setIsCompleted(targetStep.completed);

      if (isExpanded) {
        targetStep.nonDirectedChatSupportContext &&
          dispatch({
            type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
            nonDirectedChatSupportContext:
              targetStep.nonDirectedChatSupportContext,
          });
      }
    }
  }, [targetStep, state.currentStepNum, targetStepIdx]);

  /**
   * Render the step header with the title and its step number.
   * @param titleText The title of the step.
   * @param toggle The toggle function that will be used to expand/collapse the expander by clicking the header.
   */
  const renderStepHeader = ({
    titleText,
    toggle,
  }: {
    titleText?: string;
    toggle: () => void;
  }) => {
    return getStepHeader(
      targetStepIdx,
      isCompleted,
      isExpanded,
      titleText,
      toggle
    );
  };

  /**
   * Submits Snowplow metrics to be used when the Next button is clicked.
   */
  const createContinueJourneyMetrics = () => {
    /**
     * This metric will be submitted to Snowplow when the Next button is clicked **AND** DAY1_ONBOARDING_STEPS[0].continueByVerificationStatusMetrics
     * is mapped to a particular document verification status. For example, to submit a NEXT button click for a successful Auto IDV, given this configuration in
     * DAY1_ONBOARDING_STEPS[0].continueByVerificationStatusMetrics
     *
     * {
     *         verificationStatus: VerificationStatus.AUTOMATED_IDV_SUCCESS,
     *         se: {
     *           category: Day1EventCategory.AutomatedRivOutcome,
     *           action: Day1EventAction.Continue,
     *           label: Day1EventLabel.ResultPass,
     *         },
     * }
     *
     * The below line searches for metrics associated to the current docsVerificationStatus and then submits them to Snowplow.
     */
    const journeyMetricsFromVerificationStatus =
      targetStep.continueByVerificationStatusMetrics?.find(
        (m) => m.verificationStatus === state.docsVerificationStatus
      );

    journeyMetricsFromVerificationStatus &&
      trackStructEvent({ ...journeyMetricsFromVerificationStatus.se });

    targetStep.eventCategory &&
      trackStructEvent({
        category: targetStep.eventCategory,
        action: Day1EventAction.Continue,
      });
  };

  /**
   * Set the isCompleted boolean flag to true and increment the current active step by 1.
   * If the step registered a {@link StepNextGuard}, run it first: the step
   * stays put when the guard resolves false (fail open on guard errors, and
   * re-entrant clicks are ignored while a guard is in flight).
   */
  const handleNext = async (): Promise<void> => {
    if (nextGuardRef.current) {
      if (isNextGuardPending) {
        return;
      }
      setIsNextGuardPending(true);
      let proceed = true;
      try {
        proceed = await nextGuardRef.current();
      } catch (error) {
        void Logger.errorErrorLike(
          "Step next-guard threw; proceeding (fail open)",
          error as Error
        );
      } finally {
        setIsNextGuardPending(false);
      }
      if (!proceed) {
        return;
      }
    }
    createContinueJourneyMetrics();
    setIsCompleted(true);
    setIsExpanded(false);
    if (targetStep) {
      if (
        targetStep.id === STEP_ID.AMAZON_USERNAME &&
        !isUsbDetectionAvailable()
      ) {
        const OS = getOS();
        const enabledSteps = getEnabledSteps(state.steps);
        const insertAndTestSecurityKeyStepEnabled = enabledSteps.some(
          (step) => step.id === STEP_ID.INSERT_AND_TEST_SECURITY_KEY
        );

        if (!insertAndTestSecurityKeyStepEnabled) {
          try {
            publishInsertAndTestSecurityKeyStepBypassedMetric(
              OS,
              navigator.userAgent
            );
          } catch (error) {
            // Silently ignore metric publishing errors to prevent breaking navigation
          }
        }
      }

      dispatch({
        type: ActionType.NEXT_STEP,
        stepId: targetStep.id,
      });
      props.handleNext?.();
    }
    try {
      window.electronAPI.closeRightView();
    } catch (error) {
      // Ignore since the Electron API is available on the desktop app only
    }
  };

  if (targetStep) {
    return (
      <div className={activeStepClassName} data-test-id="stepContainerTestId">
        <View padding={day1StepsGridViewPadding} dataTestId={props.dataTestId}>
          <Expander
            renderTitle={renderStepHeader}
            titleText={intl.formatMessage({ id: targetStep.titleId })}
            isExpanded={targetStep.expandable && isExpanded}
            dataTestId="expanderDataTestId"
          >
            <Hr />
            <Spacer height={15} />
            <StepNextGuardContext.Provider value={setNextGuard}>
              {props.children}
            </StepNextGuardContext.Provider>
            <Spacer height="S400" />
            {!targetStep.hideNext && (
              <Row justifyContent="flex-end" className="wizard-next-btn-row">
                <NextButton
                  buttonTextI18nId={targetStep.nextButtonTitleId}
                  dataTestId={`${props.stepId}NextBtn`}
                  onClick={() => void handleNext()}
                  disabled={targetStep.nextDisabled || isNextGuardPending}
                  disabledTextI18nId={targetStep.nextButtonDisabledI18nId}
                />
              </Row>
            )}
          </Expander>
        </View>
      </div>
    );
  } else {
    void Logger.error(
      `Error in showing the step in the list - targetStepIdx: ${targetStepIdx}, state: ${JSON.stringify(
        state
      )} and stepId: ${props.stepId}`
    );
    return null;
  }
}
